import {
  MISSION_STATUS_ENUM,
  MISSION_DISPLAY_STATUS_READY_TO_PUBLISH,
} from '../../shared/constants'
import { forecastWeekKeys, isoWeekKey, parseIsoWeekKey } from './weeks'

/**
 * Rosters from the API's per-week counts instead of from every mission.
 *
 * The roster list, its status filter and the counters used to be derived by
 * walking the whole mission array (Utils/Mission mockRosters, CityPlanView
 * getFilteredRosters, DetailsSection getDetailsItems). These do the same
 * arithmetic on `missionStats`, so the page never needs the missions.
 */

const countFor = (week, status) => {
  const entry = (week.byStatus || []).find(item => item.status === status)
  return entry ? entry.count : 0
}

/**
 * The status a roster shows, by the same rules CityPlanView's filter and
 * RosterView's isRosterReadyToPublish used on the mission array:
 *  - AVAILABLE when any mission is published (AVAILABLE or beyond)
 *  - READY_TO_PUBLISH when every mission is a signed-off draft
 *  - DRAFT when all missions are drafts and none is signed off
 *  - null for an empty week
 * @param {object} week One entry of stats.weeks
 * @returns {?string}
 */
export const rosterStatus = week => {
  if (!week.total) return null
  const drafts = countFor(week, MISSION_STATUS_ENUM.DRAFT)
  if (drafts < week.total) return MISSION_STATUS_ENUM.AVAILABLE
  if (week.readyCount === week.total)
    return MISSION_DISPLAY_STATUS_READY_TO_PUBLISH
  return MISSION_STATUS_ENUM.DRAFT
}

/**
 * @param {object} week One entry of stats.weeks
 * @returns {object} A roster as RostersTable / CityPlanView consume it
 */
export const rosterFromWeek = week => ({
  id: week.key,
  key: week.key,
  year: String(week.isoYear),
  weekNum: String(week.isoWeek),
  startDate: week.startDate,
  endDate: week.endDate,
  total: week.total,
  readyCount: week.readyCount,
  withUserCount: week.withUserCount,
  completed:
    countFor(week, MISSION_STATUS_ENUM.COMPLETED) +
    countFor(week, MISSION_STATUS_ENUM.PAID),
  byStatus: week.byStatus || [],
  status: rosterStatus(week),
  isEmptyPlaceholder: Boolean(week.isEmptyPlaceholder),
})

/**
 * Rosters for the overview, oldest first, with the current week and the two
 * after it always present (the API pads them too; this is a belt-and-braces
 * for a stale response).
 * @param {?object} stats CityPlanMissionStats
 * @param {Date} [now]
 * @returns {Array<object>}
 */
export const rostersFromStats = (stats, now = new Date()) => {
  const byKey = new Map()
  ;((stats && stats.weeks) || []).forEach(week => {
    byKey.set(week.key, rosterFromWeek(week))
  })
  forecastWeekKeys(now).forEach(key => {
    if (byKey.has(key)) return
    const { isoYear, isoWeek } = parseIsoWeekKey(key)
    byKey.set(
      key,
      rosterFromWeek({
        key: isoWeekKey(isoYear, isoWeek),
        isoYear,
        isoWeek,
        total: 0,
        readyCount: 0,
        withUserCount: 0,
        byStatus: [],
        isEmptyPlaceholder: true,
      }),
    )
  })
  return Array.from(byKey.values()).sort(
    (a, b) =>
      Number(a.year) - Number(b.year) || Number(a.weekNum) - Number(b.weekNum),
  )
}

/**
 * The overview's All / Published / Draft / Ready filter, on rosters.
 * @param {Array<object>} rosters From rostersFromStats
 * @param {?string} statusFilter null, 'AVAILABLE', 'DRAFT' or 'READY_TO_PUBLISH'
 * @returns {Array<object>}
 */
export const filterRostersByStatus = (rosters, statusFilter) => {
  if (!statusFilter) return rosters
  return rosters.filter(roster => roster.status === statusFilter)
}

/**
 * The counters DetailsSection shows, from stats rather than from missions.
 * Mirrors DetailsSection/utils getDetailsItems: total, then one line per
 * status, with signed-off drafts counted as READY_TO_PUBLISH.
 * @param {?object} stats CityPlanMissionStats or one of its weeks
 * @returns {Array<{label: string, value: number}>}
 */
export const detailsItemsFromStats = stats => {
  if (!stats) return [{ label: '# Missions', value: 0 }]
  const items = [{ label: '# Missions', value: stats.total || 0 }]
  ;(stats.byStatus || []).forEach(({ status, count }) => {
    if (status === MISSION_STATUS_ENUM.DRAFT) {
      const ready = stats.readyCount || 0
      if (count - ready > 0)
        items.push({ label: `# ${status} Missions`, value: count - ready })
      if (ready > 0)
        items.push({
          label: `# ${MISSION_DISPLAY_STATUS_READY_TO_PUBLISH} Missions`,
          value: ready,
        })
      return
    }
    items.push({ label: `# ${status} Missions`, value: count })
  })
  return items
}
